import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link, Mail, Loader2, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getOAuthLinkConfirmation, confirmOAuthLink } from '@/api/auth';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string | null;
}

export function OAuthLinkConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session');

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    provider: string;
    email: string;
    existing_user: User;
  } | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Invalid session. Please try signing in again.');
      setLoading(false);
      return;
    }

    loadLinkConfirmation();
  }, [sessionId]);

  const loadLinkConfirmation = async () => {
    try {
      const response = await getOAuthLinkConfirmation(sessionId!);

      if (response.success) {
        setData({
          provider: response.provider!,
          email: response.email!,
          existing_user: response.existing_user!
        });
      } else {
        setError(response.error || 'Failed to load account linking information');
      }
    } catch (error) {
      setError('Failed to load account linking information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLink = async () => {
    if (!sessionId) return;

    setConfirming(true);
    setError(null);

    try {
      const response = await confirmOAuthLink(sessionId);

      if (response.success && response.redirect) {
        // Redirect to OAuth callback to complete the flow
        window.location.href = response.redirect;
      } else {
        setError(response.error || 'Failed to link accounts');
      }
    } catch (error) {
      setError('Failed to link accounts. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = () => {
    // Clear the OAuth session and redirect to login
    navigate('/auth/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading account linking information...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/images/doploy-logo.png"
              alt="Doploy"
              className="h-8 mx-auto mb-4"
            />
          </div>

          {/* Error Card */}
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-4">
              <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-2xl">Account Linking Error</CardTitle>
            </CardHeader>

            <CardContent className="text-center space-y-6">
              <Alert variant="destructive">
                <X className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>

              <Button onClick={() => navigate('/auth/login')} className="w-full">
                Return to Login
              </Button>

              <p className="text-xs text-gray-500">
                Need help? Contact us at{' '}
                <a
                  href="mailto:support@doploy.io"
                  className="text-blue-600 hover:text-blue-800"
                >
                  support@doploy.io
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/doploy-logo.png"
            alt="Doploy"
            className="h-8 mx-auto mb-4"
          />
        </div>

        {/* Main Card */}
        <Card className="shadow-lg">
          <CardHeader className="text-center pb-4">
            <Link className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <CardTitle className="text-2xl">
              Link your {data.provider.charAt(0).toUpperCase() + data.provider.slice(1)} account?
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Info Alert */}
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Account Found</strong><br />
                We found an existing doploy account with the email{' '}
                <strong>{data.existing_user.email}</strong>
              </AlertDescription>
            </Alert>

            {/* Explanation */}
            <div className="space-y-4">
              <p className="text-gray-600">
                Your {data.provider.charAt(0).toUpperCase() + data.provider.slice(1)} account uses the email{' '}
                <strong className="text-gray-900">{data.email}</strong>.
              </p>

              <p className="text-gray-600">
                Would you like to link your {data.provider.charAt(0).toUpperCase() + data.provider.slice(1)} account to your existing doploy account?
                This will allow you to sign in using either method.
              </p>
            </div>

            {/* Existing Account Display */}
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-medium">
                    {data.existing_user.email.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {data.existing_user.email}
                      </p>
                    </div>

                    <p className="text-sm text-gray-600 mt-1">
                      {data.existing_user.full_name || 'Existing doploy account'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleConfirmLink}
                disabled={confirming}
                className="w-full"
              >
                {confirming ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Linking Accounts...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Yes, Link My {data.provider.charAt(0).toUpperCase() + data.provider.slice(1)} Account
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={confirming}
                className="w-full"
              >
                No, Use Different Account
              </Button>
            </div>

            {/* Footer */}
            <div className="text-center pt-4">
              <p className="text-xs text-gray-500">
                Need help? Contact us at{' '}
                <a
                  href="mailto:support@doploy.io"
                  className="text-blue-600 hover:text-blue-800"
                >
                  support@doploy.io
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
